import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '../lib/supabase'
import type { ProductPackagingItem, ProductPackagingItemInsert } from '../lib/database.types'

const PKG_KEY = (productId: string) => ['product_packaging_items', productId]

export function useProductPackaging(productId: string | undefined) {
  return useQuery({
    queryKey: PKG_KEY(productId ?? ''),
    enabled: !!productId,
    queryFn: async (): Promise<ProductPackagingItem[]> => {
      const { data, error } = await supabase
        .from('product_packaging_items')
        .select('*')
        .eq('product_id', productId!)
        .order('sort_order', { ascending: true })
        .order('created_at', { ascending: true })
      if (error) throw error
      return data ?? []
    },
  })
}

export function useReplaceProductPackaging(productId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (items: Array<Omit<ProductPackagingItemInsert, 'user_id' | 'product_id'>>): Promise<void> => {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) throw new Error('not authenticated')
      // Throw on failure — otherwise stale child rows survive and the
      // re-insert below would partial-save or hit RLS conflicts silently.
      const { error: delErr } = await supabase
        .from('product_packaging_items')
        .delete()
        .eq('product_id', productId)
      if (delErr) throw delErr
      if (items.length > 0) {
        const rows = items.map((it, i) => ({
          ...it,
          user_id: user.id,
          product_id: productId,
          sort_order: i,
        }))
        const { error } = await supabase.from('product_packaging_items').insert(rows)
        if (error) throw error
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PKG_KEY(productId) })
    },
  })
}
